/* BD HERBS — product page: gallery, variant pills, add to cart, buy now, share, sticky bar, lightbox, tabs. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format, bnDigits) {
    const value = Number(cents) || 0;
    const fmt = (precision, thousands = ',', decimal = '.') => {
      const [whole, frac] = (value / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '{{amount}}').match(pattern);
    const map = {
      amount: () => fmt(2),
      amount_no_decimals: () => fmt(0),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.'),
      amount_with_apostrophe_separator: () => fmt(2, "'"),
      amount_no_decimals_with_space_separator: () => fmt(0, ' '),
      amount_with_space_separator: () => fmt(2, ' ', ','),
      amount_with_period_and_space_separator: () => fmt(2, ' ', '.'),
    };
    const out = match ? format.replace(pattern, (map[match[1]] || map.amount)()) : fmt(2);
    return bnDigits ? toBnDigits(out) : out;
  }

  const toBnDigits = (text) => String(text).replace(/\d/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);

  /* ======================================================================
     <bd-product>
     ====================================================================== */
  if (!customElements.get('bd-product')) {
    customElements.define(
      'bd-product',
      class BdProduct extends HTMLElement {
        connectedCallback() {
          // Reconnected (theme editor moving sections): only the window listeners were removed
          if (this.bound) {
            if (this.onStickyScroll) {
              window.addEventListener('scroll', this.onStickyScroll, { passive: true });
              window.addEventListener('resize', this.onStickyScroll, { passive: true });
              this.onStickyScroll();
            }
            return;
          }
          const json = this.querySelector('[data-product-json]');
          if (!json) return;
          this.bound = true;
          this.data = JSON.parse(json.textContent);
          this.labels = this.data.labels;
          this.cartUrl = this.dataset.cartUrl || '/cart';

          this.form = this.querySelector('[data-product-form]');
          this.variantInput = this.querySelector('[data-variant-input]');
          this.qtyInput = this.querySelector('[data-qty]');
          this.addButton = this.querySelector('[data-add]');
          this.addLabel = this.querySelector('[data-add-label]');
          this.buyLink = this.querySelector('[data-buy]');
          this.buyLabel = this.querySelector('[data-buy-label]');
          this.stickyBuy = this.querySelector('[data-buy-sticky]');
          this.errorEl = this.querySelector('[data-error]');
          this.fieldsets = [...this.querySelectorAll('[data-option]')];

          const startId = Number(this.variantInput && this.variantInput.value);
          this.variant = this.data.variants.find((v) => v.id === startId) || this.data.variants[0] || null;

          this.setupGallery();
          this.setupOptions();
          this.setupQuantity();
          this.setupCart();
          this.setupShare();
          this.setupSticky();
          this.setupLightbox();
          if (this.fieldsets.length) this.updateAvailability(this.selectedValues());
        }

        disconnectedCallback() {
          if (this.onStickyScroll) {
            window.removeEventListener('scroll', this.onStickyScroll);
            window.removeEventListener('resize', this.onStickyScroll);
          }
          document.documentElement.style.removeProperty('--bd-sticky-offset');
        }

        money(cents) {
          return formatMoney(cents, this.data.moneyFormat, this.data.bnDigits);
        }

        /* ---------- Gallery ---------- */
        setupGallery() {
          this.track = this.querySelector('[data-gallery-track]');
          if (!this.track) return;
          this.slides = [...this.track.children];
          this.thumbList = this.querySelector('[data-thumbs]');
          this.thumbs = [...this.querySelectorAll('[data-thumb]')];

          const initial = this.slides.findIndex((slide) => slide.hasAttribute('data-initial'));
          if (initial > 0) requestAnimationFrame(() => this.goTo(initial, false));

          this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb))));

          this.track.addEventListener(
            'scroll',
            () => {
              cancelAnimationFrame(this.galleryFrame);
              this.galleryFrame = requestAnimationFrame(() => {
                const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
                // While a thumbnail/variant-triggered smooth scroll is running, ignore the in-between positions
                if (this.scrollTarget != null) {
                  if (index !== this.scrollTarget) return;
                  this.scrollTarget = null;
                }
                this.setActiveSlide(index);
              });
            },
            { passive: true }
          );
          // A real swipe always wins over a pending programmatic scroll
          ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
            this.track.addEventListener(type, () => (this.scrollTarget = null), { passive: true })
          );

          this.track.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.goTo((this.activeIndex || 0) + 1);
            if (event.key === 'ArrowLeft') this.goTo((this.activeIndex || 0) - 1);
          });
        }

        goTo(index, smooth = true) {
          if (!this.slides || !this.slides[index]) return;
          this.scrollTarget = index;
          this.track.scrollTo({
            left: index * this.track.clientWidth,
            behavior: smooth && !reduceMotion() ? 'smooth' : 'auto',
          });
          this.setActiveSlide(index);
        }

        setActiveSlide(index) {
          if (index === this.activeIndex) return;
          this.activeIndex = index;
          this.thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
            if (i === index) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
          this.scrollThumbIntoView(index);
        }

        // Centre the active thumbnail inside its own row (never scrolls the page)
        scrollThumbIntoView(index) {
          const list = this.thumbList;
          const item = this.thumbs[index] && this.thumbs[index].parentElement;
          if (!list || !item || list.scrollWidth <= list.clientWidth) return;
          const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
          list.scrollTo({ left: Math.max(0, left), behavior: reduceMotion() ? 'auto' : 'smooth' });
        }

        showVariantMedia() {
          if (!this.variant || !this.variant.media || !this.slides) return;
          const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === this.variant.media);
          if (index >= 0) this.goTo(index);
        }

        /* ---------- Variant pills ---------- */
        setupOptions() {
          if (!this.form || !this.fieldsets.length) return;
          this.form.addEventListener('change', (event) => {
            if (!event.target.classList.contains('bd-opt__input')) return;
            this.onOptionChange();
          });
        }

        selectedValues() {
          return this.fieldsets.map((fieldset) => {
            const checked = fieldset.querySelector('.bd-opt__input:checked');
            return checked ? checked.value : null;
          });
        }

        onOptionChange() {
          const values = this.selectedValues();
          this.fieldsets.forEach((fieldset, i) => {
            const current = fieldset.querySelector('[data-option-current]');
            if (current) current.textContent = values[i];
          });

          this.variant = this.data.variants.find((v) => v.options.every((option, i) => option === values[i])) || null;
          this.updateAvailability(values);
          this.render();

          if (this.variant) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', this.variant.id);
            window.history.replaceState({}, '', url.toString());
            this.showVariantMedia();
          }

          // Same signals as Dawn, for apps (COD forms etc.) that follow the selected variant
          if (this.variantInput) this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          document.dispatchEvent(
            new CustomEvent('variant:change', { detail: { variant: this.variant, id: this.variant ? this.variant.id : null } })
          );
        }

        // Grey out values that are sold out (or don't exist) together with the other current choices
        updateAvailability(values) {
          this.fieldsets.forEach((fieldset, index) => {
            fieldset.querySelectorAll('.bd-opt__input').forEach((input) => {
              const candidate = [...values];
              candidate[index] = input.value;
              const match = this.data.variants.find((v) =>
                candidate.every((value, i) => value === null || v.options[i] === value)
              );
              const available = Boolean(match && match.available);
              input.classList.toggle('is-unavailable', !available);

              const label = input.nextElementSibling;
              const note = label && label.querySelector('.visually-hidden');
              if (note) note.remove();
              if (label && !available) {
                const span = document.createElement('span');
                span.className = 'visually-hidden';
                span.textContent = ` (${this.labels.soldOut})`;
                label.appendChild(span);
              }
            });
          });
        }

        /* ---------- Render price / stock / buttons for the current variant ---------- */
        render() {
          const v = this.variant;
          if (this.variantInput) this.variantInput.value = v ? v.id : '';

          // Price
          const priceNow = this.querySelector('[data-price-now]');
          const priceWas = this.querySelector('[data-price-was]');
          const priceOff = this.querySelector('[data-price-off]');
          const wasLabel = this.querySelector('[data-was-label]');
          const priceLabel = this.querySelector('[data-price-label]');
          if (v && priceNow) {
            const onSale = v.compare > v.price;
            priceNow.textContent = this.money(v.price);
            if (priceWas) priceWas.hidden = !onSale;
            if (priceOff) priceOff.hidden = !onSale;
            if (wasLabel) wasLabel.hidden = !onSale;
            if (priceLabel) priceLabel.textContent = onSale ? this.labels.salePrice : this.labels.regularPrice;
            if (onSale && priceWas && priceOff) {
              priceWas.textContent = this.money(v.compare);
              const pct = Math.round(((v.compare - v.price) * 100) / v.compare);
              priceOff.textContent = `-${this.data.bnDigits ? toBnDigits(pct) : pct}%`;
            }
            const stickyPrice = this.querySelector('[data-sticky-price]');
            if (stickyPrice) stickyPrice.textContent = this.money(v.price);
          }

          // Low-stock pill
          const low = this.querySelector('[data-low-stock]');
          if (low) low.hidden = !(v && v.low && v.available);
          const pills = this.querySelector('[data-pills]');
          if (pills) pills.hidden = ![...pills.children].some((child) => !child.hidden);

          // Stock line
          const inStock = this.querySelector('[data-stock-in]');
          const outStock = this.querySelector('[data-stock-out]');
          const available = Boolean(v && v.available);
          if (inStock) inStock.hidden = !available;
          if (outStock) {
            outStock.hidden = available;
            outStock.textContent = v ? this.labels.soldOut : this.labels.unavailable;
          }

          // Buttons
          if (this.addButton) {
            this.addButton.dataset.locked = String(!available);
            this.addButton.disabled = !available;
          }
          let buyText = this.labels.buy;
          if (!v) buyText = this.labels.unavailable;
          else if (!v.available) buyText = this.labels.soldOut;
          if (this.buyLabel) this.buyLabel.textContent = buyText;
          this.updateBuyLinks();
          this.showError('');
        }

        updateBuyLinks() {
          const v = this.variant;
          const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
          [this.buyLink, this.stickyBuy].forEach((link) => {
            if (!link) return;
            if (v && v.available) {
              // Cart permalink: goes straight to checkout with this variant and quantity
              link.href = `${this.cartUrl}/${v.id}:${qty}`;
              link.removeAttribute('aria-disabled');
              link.removeAttribute('role');
            } else {
              link.removeAttribute('href');
              link.setAttribute('role', 'link');
              link.setAttribute('aria-disabled', 'true');
            }
          });
        }

        /* ---------- Quantity ---------- */
        setupQuantity() {
          if (!this.qtyInput) return;
          this.querySelectorAll('[data-step]').forEach((button) =>
            button.addEventListener('click', () => {
              const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + Number(button.dataset.step));
              this.qtyInput.value = next;
              this.updateBuyLinks();
            })
          );
          this.qtyInput.addEventListener('change', () => {
            this.qtyInput.value = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
            this.updateBuyLinks();
          });
        }

        /* ---------- Add to cart (Ajax, opens the cart drawer) ---------- */
        setupCart() {
          if (!this.form) return;
          this.form.addEventListener('submit', async (event) => {
            if (!window.BDCart || !this.variant) return; // no JS helper → normal form post to /cart/add
            event.preventDefault();
            if (!this.variant.available) return;
            const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
            const ok = await window.BDCart.add(this.variant.id, qty, this.addButton, this.errorEl);
            if (ok) this.flashAdded();
          });
        }

        flashAdded() {
          if (!this.addLabel) return;
          clearTimeout(this.addedTimer);
          this.addLabel.textContent = this.labels.added;
          this.addedTimer = setTimeout(() => {
            this.addLabel.textContent = this.labels.add;
          }, 2400);
        }

        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent = message || '';
          this.errorEl.hidden = !message;
        }

        /* ---------- Share: copy link ---------- */
        setupShare() {
          const button = this.querySelector('[data-copy-link]');
          const status = this.querySelector('[data-copied]');
          if (!button) return;
          button.addEventListener('click', async () => {
            const url = button.dataset.copyLink;
            try {
              if (!navigator.clipboard) throw new Error('No clipboard API');
              // Some browsers leave the promise pending (permission prompt) — fall back after 1s
              await Promise.race([
                navigator.clipboard.writeText(url),
                new Promise((resolve, reject) => setTimeout(() => reject(new Error('Clipboard timeout')), 1000)),
              ]);
            } catch (error) {
              const field = document.createElement('textarea');
              field.value = url;
              field.setAttribute('readonly', '');
              field.style.position = 'fixed';
              field.style.opacity = '0';
              document.body.appendChild(field);
              field.select();
              document.execCommand('copy');
              field.remove();
            }
            if (status) {
              status.textContent = this.labels.copied;
              clearTimeout(this.copiedTimer);
              this.copiedTimer = setTimeout(() => (status.textContent = ''), 2500);
            }
          });
        }

        /* ---------- Mobile sticky bar: shows once the buttons have scrolled out of view ---------- */
        // A scroll check (not IntersectionObserver): IO misses jumps from "above the screen"
        // straight to "below the screen", e.g. back-to-top, which would leave the bar stuck open.
        setupSticky() {
          this.sticky = this.querySelector('[data-sticky]');
          const anchor = this.querySelector('[data-buttons]') || this.buyLink;
          if (!this.sticky || !anchor) return;
          const mobile = window.matchMedia('(max-width: 989px)');
          let visibleNow = false;

          const setVisible = (visible) => {
            if (visible === visibleNow) return;
            visibleNow = visible;
            this.sticky.classList.toggle('is-visible', visible);
            this.sticky.toggleAttribute('inert', !visible);
            this.sticky.setAttribute('aria-hidden', String(!visible));
            if (visible) {
              document.documentElement.style.setProperty('--bd-sticky-offset', `${this.sticky.offsetHeight}px`);
            } else {
              document.documentElement.style.removeProperty('--bd-sticky-offset');
            }
          };

          // One cheap layout read per scroll event; setVisible() only touches the DOM when the state flips
          const check = () => {
            const scrolledPast = anchor.getBoundingClientRect().bottom < 0;
            setVisible(mobile.matches && scrolledPast);
          };
          this.onStickyScroll = check;
          window.addEventListener('scroll', this.onStickyScroll, { passive: true });
          window.addEventListener('resize', this.onStickyScroll, { passive: true });
          mobile.addEventListener('change', check);
          check();
        }

        /* ---------- Full-screen viewer ---------- */
        setupLightbox() {
          this.lightbox = this.querySelector('[data-lightbox]');
          if (!this.lightbox) return;
          this.lightboxTrack = this.lightbox.querySelector('[data-lightbox-track]');
          const slides = [...this.lightboxTrack.children];

          this.querySelectorAll('[data-open-lightbox]').forEach((button) =>
            button.addEventListener('click', () => {
              const mediaIndex = Number(button.dataset.openLightbox);
              const target = slides.findIndex((slide) => Number(slide.dataset.index) === mediaIndex);
              this.lastFocus = button;
              document.body.classList.add('overflow-hidden');
              this.lightbox.showModal();
              this.lightboxTrack.scrollTo({ left: Math.max(0, target) * this.lightboxTrack.clientWidth, behavior: 'auto' });
              this.lightbox.querySelector('[data-lightbox-close]').focus();
            })
          );

          this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => {
            this.lightbox.close();
            document.body.classList.remove('overflow-hidden');
          });
          this.lightbox.addEventListener('close', () => {
            document.body.classList.remove('overflow-hidden');
            slides.forEach((slide) => slide.classList.remove('is-zoomed'));
            if (this.lastFocus) this.lastFocus.focus({ preventScroll: true });
          });

          this.lightbox.querySelectorAll('[data-lightbox-step]').forEach((button) =>
            button.addEventListener('click', () => {
              this.lightboxTrack.scrollBy({
                left: Number(button.dataset.lightboxStep) * this.lightboxTrack.clientWidth,
                behavior: reduceMotion() ? 'auto' : 'smooth',
              });
            })
          );

          // Click / tap toggles a 2x zoom centred on the pointer; pinch-zoom also works on phones
          this.lightboxTrack.addEventListener('click', (event) => {
            const image = event.target.closest('.bd-lightbox__image');
            if (!image) return;
            const slide = image.closest('.bd-lightbox__slide');
            const rect = image.getBoundingClientRect();
            image.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
            slide.classList.toggle('is-zoomed');
          });
          this.lightboxTrack.addEventListener('scroll', () => slides.forEach((slide) => slide.classList.remove('is-zoomed')), {
            passive: true,
          });
        }
      }
    );
  }

  /* ======================================================================
     <bd-tabs>: tabs on tablet/desktop, accordion on phones
     ====================================================================== */
  if (!customElements.get('bd-tabs')) {
    customElements.define(
      'bd-tabs',
      class BdTabs extends HTMLElement {
        connectedCallback() {
          if (this.bound) return; // a second set of listeners would open + close in one tap
          this.bound = true;
          this.tabs = [...this.querySelectorAll('[role="tab"]')];
          this.items = [...this.querySelectorAll('.bd-tabs__item')];
          this.panels = this.items.map((item) => item.querySelector('.bd-tabs__panel'));
          this.toggles = this.items.map((item) => item.querySelector('.bd-tabs__acc'));
          this.active = 0;
          this.open = new Set([0]);
          this.desktop = window.matchMedia('(min-width: 750px)');

          this.tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => this.select(i));
            tab.addEventListener('keydown', (event) => {
              const keys = { ArrowRight: 1, ArrowLeft: -1 };
              if (!(event.key in keys)) return;
              event.preventDefault();
              const next = (i + keys[event.key] + this.tabs.length) % this.tabs.length;
              this.select(next);
              this.tabs[next].focus();
            });
          });

          this.toggles.forEach((toggle, i) =>
            toggle.addEventListener('click', () => {
              if (this.open.has(i)) this.open.delete(i);
              else this.open.add(i);
              this.apply();
            })
          );

          this.desktop.addEventListener('change', () => this.apply());
          this.apply();
        }

        select(index) {
          this.active = index;
          this.apply();
        }

        apply() {
          const desktop = this.desktop.matches;
          this.tabs.forEach((tab, i) => {
            const selected = i === this.active;
            tab.setAttribute('aria-selected', String(selected));
            tab.tabIndex = selected ? 0 : -1;
          });
          this.panels.forEach((panel, i) => {
            panel.hidden = desktop ? i !== this.active : !this.open.has(i);
            if (desktop) {
              panel.setAttribute('role', 'tabpanel');
              if (this.tabs[i]) panel.setAttribute('aria-labelledby', this.tabs[i].id);
            } else {
              panel.removeAttribute('role');
              panel.removeAttribute('aria-labelledby');
            }
          });
          this.toggles.forEach((toggle, i) => toggle.setAttribute('aria-expanded', String(this.open.has(i))));
        }
      }
    );
  }
})();
